import { useRef, useState } from "react";
import { useIsFetching, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "../../components/ui/button";
import { QueueLayout } from "../../features/web3/forex/components/QueueLayout";
import { QueueTable, Td } from "../../features/web3/forex/components/table";
import { adminDelete, adminPatch, adminPost, useCan, useForexAdmin } from "./api";
import { Drawer, Panel, QueryState, inputCls, labelCls, mono } from "./parts";

// 025: the two starting roles have fixed ids and can never be deleted.
const STARTING = ["6f0e4d3a-1c2b-4a59-8e7d-000000000001", "6f0e4d3a-1c2b-4a59-8e7d-000000000002"];

const errBox = "rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300";

// Labels come from GET /roles/admin/permissions; a name it does not list
// (a permission a later release dropped) shows as itself.
export const labelOf = (perms, name) => perms?.find((p) => p.name === name)?.label ?? name;

// Both lists answer to roles.manage or operators.manage (025).
export const usePermissionList = () => useForexAdmin("/roles/admin/permissions");
export const useRoles = () => useForexAdmin("/roles/admin");

// Create (role null) or edit one role. The permissions list is the whole set:
// PATCH replaces it. No optimistic UI, since the lockout guard's 409 changes nothing.
function RoleDrawer({ role, open, onClose, perms }) {
  const qc = useQueryClient();
  const [name, setName] = useState(role?.name ?? "");
  const [picked, setPicked] = useState(role?.permissions ?? []);
  // A create that got no answer (timeout, network, 5xx) may still have landed.
  const unanswered = useRef(false);
  const done = () => {
    // Also refetches /me, since editing the caller's own role changes what they hold.
    qc.invalidateQueries({ queryKey: ["forex-admin"] });
    onClose();
  };
  const m = useMutation({
    mutationFn: (body) => (role ? adminPatch(`/roles/admin/${role.id}`, body) : adminPost("/roles/admin", body)),
    onSuccess: done,
    onError: (err) => {
      // 025: a repeated POST /roles/admin is a 409 on its name; after an
      // unanswered try, read it as done. A first-try 409 is a real clash.
      if (!role && unanswered.current && err.status === 409) return done();
      if (err.status === 0 || err.status >= 500) unanswered.current = true;
    },
  });
  const toggle = (p) => setPicked((now) => (now.includes(p) ? now.filter((x) => x !== p) : [...now, p]));
  // Kept in the permissions list's order, as every answer lists them.
  const body = () => ({
    name: name.trim(),
    permissions: perms.map((p) => p.name).filter((p) => picked.includes(p)),
  });
  // An unchanged save still lands on the audit trail (026), so it is not offered.
  const changed =
    !role ||
    name.trim() !== role.name ||
    JSON.stringify(body().permissions) !== JSON.stringify(role.permissions);

  return (
    <Drawer open={open} onClose={onClose} title={role ? `Edit ${role.name}` : "New role"}>
      <form
        className="space-y-5"
        onSubmit={(e) => {
          e.preventDefault();
          m.mutate(body());
        }}
      >
        <div>
          <label className={labelCls}>Name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={100} className={inputCls} />
        </div>
        <div className="space-y-2">
          <label className={labelCls}>Permissions</label>
          {perms.map((p) => (
            <label key={p.name} className="flex items-center gap-3 text-sm">
              <input type="checkbox" checked={picked.includes(p.name)} onChange={() => toggle(p.name)} />
              <span>{p.label}</span>
              <span className="font-mono text-xs text-muted-foreground">{p.name}</span>
            </label>
          ))}
        </div>
        {m.error && <p className={errBox}>{m.error.message}</p>}
        <Button size="sm" type="submit" disabled={!name.trim() || !changed || m.isPending}>
          {m.isPending ? "Saving…" : role ? "Save role" : "Create role"}
        </Button>
      </form>
    </Drawer>
  );
}

function DeleteRole({ role, onClose }) {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: () => adminDelete(`/roles/admin/${role.id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["forex-admin"] });
      onClose();
    },
    // A repeated delete is a 404: it went through (025).
    onError: (err) => {
      if (err.status === 404) {
        qc.invalidateQueries({ queryKey: ["forex-admin"] });
        onClose();
      }
    },
  });
  return (
    <Drawer open={!!role} onClose={() => { m.reset(); onClose(); }} title={`Delete ${role?.name ?? "role"}`}>
      <p className="text-sm text-muted-foreground">The role is gone for good. Nobody holds it now.</p>
      {m.error && <p className={errBox}>{m.error.message}</p>}
      <Button size="sm" variant="destructive" disabled={m.isPending} onClick={() => m.mutate()}>
        {m.isPending ? "Deleting…" : "Delete role"}
      </Button>
    </Drawer>
  );
}

const COLUMNS = [{ label: "Role" }, { label: "Permissions" }, { label: "Operators", align: "right" }, { label: "" }];

export default function Roles() {
  const can = useCan();
  const manage = can("roles.manage");
  const qc = useQueryClient();
  const fetching = useIsFetching({ queryKey: ["forex-admin"] }) > 0;
  const perms = usePermissionList();
  const roles = useRoles();
  // editing: null = closed, "new" = create, a role = edit it.
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  return (
    <QueueLayout
      eyebrow="Forex Admin · Access"
      title="Roles"
      description="Each operator holds one role, a named set of permissions. A change bites on each holder's next request."
      isFetching={fetching}
      onRefresh={() => qc.invalidateQueries({ queryKey: ["forex-admin"] })}
    >
      {manage && perms.data && (
        <Button size="sm" onClick={() => setEditing("new")}>+ New role</Button>
      )}
      <Panel title="Roles">
        <QueryState q={roles}>
          {(rows) => (
            <QueueTable
              columns={COLUMNS}
              rows={rows}
              renderRow={(r) => (
                <tr key={r.id} className="align-top">
                  <Td className="whitespace-nowrap">
                    {r.name}
                    <div className="font-mono text-[10px] text-muted-foreground">{r.id}</div>
                  </Td>
                  <Td className="text-xs">
                    {r.permissions.length === 0
                      ? <span className="text-muted-foreground">None</span>
                      : r.permissions.map((p) => labelOf(perms.data, p)).join(", ")}
                  </Td>
                  <Td align="right" className={mono}>{r.operatorCount}</Td>
                  <Td className="whitespace-nowrap text-xs">
                    {manage && perms.data && (
                      <Button size="sm" variant="outline" onClick={() => setEditing(r)}>Edit</Button>
                    )}
                    {/* Starting roles are never deleted; a held role is a 409, so wait for it to empty. */}
                    {manage && !STARTING.includes(r.id) && (
                      <Button
                        size="sm"
                        variant="destructive"
                        className="ml-2"
                        disabled={r.operatorCount > 0}
                        title={r.operatorCount > 0 ? "Give its operators another role first" : undefined}
                        onClick={() => setDeleting(r)}
                      >
                        Delete
                      </Button>
                    )}
                  </Td>
                </tr>
              )}
            />
          )}
        </QueryState>
      </Panel>
      {editing && (
        <RoleDrawer
          key={editing === "new" ? "new" : editing.id}
          role={editing === "new" ? null : editing}
          open
          onClose={() => setEditing(null)}
          perms={perms.data}
        />
      )}
      {/* Keyed, so each opening starts without the last one's error. */}
      <DeleteRole key={deleting?.id} role={deleting} onClose={() => setDeleting(null)} />
    </QueueLayout>
  );
}
